import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {AgendaelementId, BeslutningId, GruppeId, GruppeModell, MuterendeOppgaveSpesifikasjon, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {TranslateModule} from "@ngx-translate/core";
import {MatToolbar} from "@angular/material/toolbar";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatIcon} from "@angular/material/icon";
import {OppgaveSkjemaKomponent} from "../../komponenter/oppgave-skjema/oppgave-skjema";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {MatButton, MatIconButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {VelgGruppeKomponent} from "../../../gruppe/komponenter/velg-gruppe/velg-gruppe";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {grupperDerBrukerHarTilgangsrolle} from "felles-ng2/modell/gruppeModellHjelpere";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {BeslutningModellDLM} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";

const debug = initDebugger('OppgaveOpprettDialogKomponent');

/**
 * Dialog for å opprette ny oppgave
 */
@Component({
    selector: 'oppgave-opprett-dialog',
    templateUrl: 'oppgave-opprett-dialog.html',
    styleUrl: 'oppgave-opprett-dialog.scss',
    host: {'data-testid': 'oppgave-dialog-skjema'},
    imports: [
        TranslateModule,
        MatToolbar,
        MatIcon,
        OppgaveSkjemaKomponent,
        MatDialogContent,
        ReactiveFormsModule,
        MatIconButton,
        VisSpinnerDirektiv,
        MatButton,
        MatDialogActions,
        VelgGruppeKomponent,
        MatFormField,
        MatLabel
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OppgaveOpprettDialogKomponent {

    static visOpprettUnderSak(dialog: MatDialog, sak: AgendaelementModellDLM): MatDialogRef<OppgaveOpprettDialogKomponent, OppgaveOpprettDialogRespons> {
        return dialog.open(OppgaveOpprettDialogKomponent, dialogArgumenter({sak}));
    }

    static visOpprettUnderBeslutning(dialog: MatDialog, beslutning: BeslutningModellDLM): MatDialogRef<OppgaveOpprettDialogKomponent, OppgaveOpprettDialogRespons> {
        return dialog.open(OppgaveOpprettDialogKomponent, dialogArgumenter({beslutning: beslutning}));
    }

    static visOpprettUnderGruppe(dialog: MatDialog): MatDialogRef<OppgaveOpprettDialogKomponent, OppgaveOpprettDialogRespons> {
        return dialog.open(OppgaveOpprettDialogKomponent, dialogArgumenter({}));
    }

    sak?: AgendaelementModellDLM; //kommer fra DialogParametre
    beslutning?: BeslutningModellDLM; //kommer fra DialogParametre

    skjema = new FormGroup({
        gruppe: new FormControl<GruppeModell | null>(null, Validators.required),
        oppgave: OppgaveSkjemaKomponent.opprettSkjema()
    });

    modus: 'sak' | 'beslutning' | 'gruppe' = 'gruppe';
    gruppeId = signal<GruppeId | null>(null);
    tilgjengeligeGrupper = signal<GruppeModellDML[]>([]);
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<OppgaveOpprettDialogKomponent, OppgaveOpprettDialogRespons>,
                private avhengigheter: OppgaveOpprettDialogKomponentAvhengigheter,
                private gjeldendeTidssone: GjeldendeTidssone) {
        //avgjør hvilket modus vi er i (opprett oppgave under sak/beslutning/frittstående)
        if (erIkkeNil(parametre.sak)) {
            this.modus = 'sak';
            this.skjema.controls.gruppe.disable({emitEvent: false});
            this.gruppeId.set(parametre.sak.eiendeGruppeId);
            this.sak = parametre.sak;
        } else if (erIkkeNil(parametre.beslutning)) {
            this.modus = 'beslutning';
            this.skjema.controls.gruppe.disable({emitEvent: false});
            this.gruppeId.set(parametre.beslutning.eiendeGruppeId);
            this.beslutning = parametre.beslutning;
        } else {
            this.modus = 'gruppe';
            this.lastGrupper();
            //oppdater gruppeId dersom valgt gruppe endres
            this.skjema.controls.gruppe.valueChanges
              .pipe(takeUntilDestroyed())
              .subscribe(gruppe => this.gruppeId.set(gruppe!.id));
        }
    }

    async opprett() {
        await this.lagrer.utfør(() => {
            switch (this.modus) {
                case 'gruppe':
                    return this.avhengigheter.opprettUnderGruppe(this.somSpesifikasjon());
                case 'sak':
                    return this.avhengigheter.opprettUnderSak(this.sak!.id, this.somSpesifikasjon());
                case 'beslutning':
                    return this.avhengigheter.opprettUnderBeslutning(this.beslutning!.id, this.somSpesifikasjon());
            }
        });
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    // private hjelpemetoder

    private lastGrupper() {
        this.avhengigheter.tilgjengeligeGrupper()
          .pipe(takeUntilDestroyed())
          .subscribe(tilgjengeligeGrupper =>
            this.tilgjengeligeGrupper.set(tilgjengeligeGrupper));
    }

    private somSpesifikasjon(): MuterendeOppgaveSpesifikasjon {
        return OppgaveSkjemaKomponent.somSpesifikasjon(this.skjema.controls.oppgave, this.gjeldendeTidssone.brukersTidssone())
    }
}

export type OppgaveOpprettDialogRespons = boolean;

/** Én av sak, beslutning eller gruppe må være angitt */
export type DialogParametre = {
    sak?: AgendaelementModellDLM;
    beslutning?: BeslutningModellDLM;
    gruppe?: GruppeId;

};

@Injectable()
export class OppgaveOpprettDialogKomponentAvhengigheter {

    constructor(private oppgaveDatatjeneste: OppgaveDatatjeneste,
                private gruppeDatatjeneste: GruppeDatatjeneste,
                private kontekst: BrukerkontekstTilbyder) {
    }

    /** Liste med grupper bruker har skrivetilgang til */
    tilgjengeligeGrupper(): Observable<GruppeModellDML[]> {
        return this.gruppeDatatjeneste.tilgjengeligeGrupper()
          .pipe(map(grupper =>
            grupperDerBrukerHarTilgangsrolle(grupper, this.kontekst.autentisertBruker(), TilgangsrolleMedlem.skrive)));
    }

    async opprettUnderGruppe(spesifikasjon: MuterendeOppgaveSpesifikasjon): Promise<void> {
        await this.oppgaveDatatjeneste.opprettUnderGruppe(spesifikasjon);
    }

    async opprettUnderSak(sakId: AgendaelementId, spesifikasjon: MuterendeOppgaveSpesifikasjon): Promise<void> {
        await this.oppgaveDatatjeneste.opprettUnderSak(sakId, spesifikasjon);
    }

    async opprettUnderBeslutning(beslutningId: BeslutningId, spesifikasjon: MuterendeOppgaveSpesifikasjon): Promise<void> {
        await this.oppgaveDatatjeneste.opprettUnderBeslutning(beslutningId, spesifikasjon);
    }
}


function dialogArgumenter(data: DialogParametre) {
    return {
        data: data,
        disableClose: true,
        ariaLabelledBy: 'oppgave-opprett-dialog-tittel',
        minWidth: 500
    };
}
